import { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Skeleton, Tooltip, Typography } from '@mui/material';
import {
  SyncProblem,
  VolumeUp,
  VolumeOff,
  WarningRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { processDeviceData } from './utils';
import { DASHBOARD_COLORS } from './styles';
import DataRow from './shared/DataRow';
import BatteryIndicator from './shared/BatteryIndicator';
import { getFirmwareStatus } from './utils';
import { getDeviceImage } from '@/utils/compass_image_helper/deviceImageMap';
import { getIsOrgOnTrial } from '@/utils/organization/getIsOrgOnTrial';
import { getCompassDeviceByMacAddress } from '@/repository/CompassDevicesDAO';

import { UserStatusPingViewModel } from '@/models/User/UserStatusPingViewModel';
import type { DashboardDeviceOverride } from '@/models/OrgCompassDevice/OrgCompassDeviceListItem';
import {
  LocationPreference,
  OrganizationOnAzure,
} from '../../../../../../generated-client/models';
import type { PvdConfigurationOnAzure } from '@/../generated-client/models';
import { OrgUser } from '@/models/User/OrgUser';
import PvdConfigPicker from '@/components/selectors/PvdConfigPicker';
import EmergencyConfigPicker from '@/components/selectors/EmergencyConfigPicker';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import DashboardSelectorStyles from './dashboard_selectors/DashboardSelectorStyles';

const SERIAL_NUMBER_SKELETON_WIDTH = 140;

interface DeviceInfoSectionProps {
  userStatus: UserStatusPingViewModel | null;
  pvdConfigurations: PvdConfigurationOnAzure[] | undefined;
  userInfo: import('../UserDashboard').UserInfo | null;
  firmwareLookup: any;
  organization: OrganizationOnAzure;
  deviceOverride?: DashboardDeviceOverride | null;
  /** Non-Admin viewers (e.g. Dispatch) get read-only config pickers — no mutation (Task 20/WD4). */
  isReadOnly?: boolean;
}

/**
 *
 * @param {DeviceInfoSectionProps} props
 * @returns
 */
const DeviceInfoSection = ({
  userStatus,
  pvdConfigurations,
  firmwareLookup,
  userInfo,
  organization,
  deviceOverride,
  isReadOnly = false,
}: DeviceInfoSectionProps) => {
  const { t } = useTranslation();
  const { emergencyConfigurations } = useOrganizationUpdaterProvider();
  const currentConfigurationObject = pvdConfigurations?.find(
    (config) => config.id === userInfo?.pvdConfigId,
  );
  const compassInfo =
    deviceOverride?.compassInfoSnapshot ?? userStatus?.dto?.compass_info;
  const compassSettings = userStatus?.dto?.compass_settings;
  const macAddress = compassInfo?.mac_address;

  const [serialNumber, setSerialNumber] = useState<string | null>(null);
  const [isSerialNumberLoading, setIsSerialNumberLoading] = useState<boolean>(
    Boolean(macAddress),
  );

  useEffect(() => {
    if (!macAddress) {
      setSerialNumber(null);
      setIsSerialNumberLoading(false);
      return;
    }

    let cancelled = false;
    setSerialNumber(null);
    setIsSerialNumberLoading(true);

    getCompassDeviceByMacAddress(macAddress)
      .then((doc) => {
        if (cancelled) return;
        setSerialNumber(doc?.serial_number ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setSerialNumber(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsSerialNumberLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [macAddress]);

  const deviceData = processDeviceData(compassInfo, compassSettings);

  const deviceImage = useMemo(
    () => getDeviceImage(deviceData.deviceName),
    [deviceData.deviceName],
  );

  const { firmwareOutdated } = getFirmwareStatus(compassInfo, firmwareLookup);

  const configList =
    pvdConfigurations?.map((config) => ({
      id: config.id,
      name: config.spec_name,
    })) ?? [];

  const emergencyConfigList =
    emergencyConfigurations?.map((config) => ({
      id: config.id,
      name: config.spec_name,
    })) ?? [];

  // Groups keyed the same way `getUserAssignedTier` expects — mirrors the
  // derivation in UserTable.tsx (Task 18) so the target user's tier resolves
  // consistently across every picker call site.
  const orgGroups = useMemo(
    () => Object.values(organization?.groups ?? {}),
    [organization],
  );

  // Config-picker gating is trial-aware (nm-3): an active org trial elevates every
  // user to Respond, so the pickers become interactive — mirrors UserTable.tsx.
  const isOrgOnTrial = useMemo(
    () => getIsOrgOnTrial(organization?.trial_end_timestamp),
    [organization?.trial_end_timestamp],
  );

  // Single OrgUser instance shared by both pickers — carries both config IDs.
  const pickerUser = new OrgUser({
    first_name: userInfo?.firstName ?? '',
    last_name: userInfo?.lastName ?? '',
    auth_id: userInfo?.userAuthId ?? '',
    id: userInfo?.orgUserAzureId ?? '',
    language_code: userInfo?.languageCode ?? 'en',
    location_preference:
      userInfo?.locationPreference ?? LocationPreference.EmergencyOnly,
    phone_number: userInfo?.phoneNumber ?? '',
    org_user_azure_id: userInfo?.orgUserAzureId ?? '',
    role: userInfo?.role ?? 'Mobile User',
    org_code: organization.org_code,
    last_interaction_timestamp: null,
    object_type: '',
    unique_key: `org_user_${userInfo?.userAuthId}`,
    unified_config_id: '',
    pvd_config_id: userInfo?.pvdConfigId ?? '',
    emergency_config_id: userInfo?.emergencyConfigId ?? '',
    should_force_apply_config: userInfo?.shouldForceApplyConfig ?? false,
    should_force_apply_pvd_config: false,
    should_force_apply_emergency_config: false,
    is_prototyper: false,
  });

  return (
    <Paper
      // @ts-ignore
      variant="contained"
      sx={{
        p: 2,
        flex: {
          xs: 1,
          md: 4,
        },
        backgroundColor: DASHBOARD_COLORS.background,
        borderRadius: 2,
      }}
    >
      {/* Device Header with Image and Specs Side by Side */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        {/* Device Image - Left Side */}
        <Box
          sx={{
            width: {
              xs: '200px',
              md: '200px',
            },
            height: { xs: 200, md: 200 },
            alignSelf: { xs: 'center', md: 'flex-start' },
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <img
            src={deviceImage}
            alt={deviceData.deviceName || t(StringKeys.COMPASS_PRO_DEVICE)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Device Specs - Right Side */}
        <Box sx={{ flex: 1, width: '100%' }}>
          {/* Title and Battery */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: '.95rem', fontWeight: 'bold' }}
            >
              {deviceData.deviceName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Compass Alert Sound Icon */}
              {deviceData.compassAlertSound === '--' ? (
                <Tooltip
                  title={t(StringKeys.COMPASS_ALERT_SOUND_STATUS_UNAVAILABLE)}
                  arrow
                >
                  <WarningRounded sx={{ color: COLORS.GREY }} />
                </Tooltip>
              ) : deviceData.compassAlertSound ? (
                <Tooltip
                  title={t(StringKeys.COMPASS_ALERT_SOUND_DISABLED)}
                  arrow
                >
                  <VolumeOff sx={{ color: COLORS.GREY }} />
                </Tooltip>
              ) : (
                <Tooltip
                  title={t(StringKeys.COMPASS_ALERT_SOUND_ENABLED)}
                  arrow
                >
                  <VolumeUp sx={{ color: DASHBOARD_COLORS.primary }} />
                </Tooltip>
              )}
              <BatteryIndicator percentage={deviceData.batteryLevel} />
            </Box>
          </Box>

          {/* Specs List - Vertical */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {userInfo && (
              <>
                <DataRow label={t(StringKeys.COMPASS_CONFIGURATION)}>
                  <DashboardSelectorStyles>
                    <PvdConfigPicker
                      user={pickerUser}
                      configList={configList}
                      orgCode={organization?.org_code}
                      groups={orgGroups}
                      isOrgOnTrial={isOrgOnTrial}
                      disabled={isReadOnly}
                    />
                  </DashboardSelectorStyles>
                </DataRow>

                <DataRow label={t(StringKeys.EMERGENCY_CONFIGURATION)}>
                  <DashboardSelectorStyles>
                    <EmergencyConfigPicker
                      user={pickerUser}
                      configList={emergencyConfigList}
                      orgCode={organization?.org_code}
                      groups={orgGroups}
                      isOrgOnTrial={isOrgOnTrial}
                      disabled={isReadOnly}
                    />
                  </DashboardSelectorStyles>
                </DataRow>
              </>
            )}

            <DataRow label={t(StringKeys.VOLTAGE_SENSITIVITY)}>
              <Typography variant="body1" fontSize=".95rem">
                {deviceData.voltageSensitivity}
              </Typography>
            </DataRow>

            <DataRow label={t(StringKeys.CURRENT_SENSITIVITY)}>
              <Typography variant="body1" fontSize=".95rem">
                {deviceData.currentSensitivity}
              </Typography>
            </DataRow>

            <DataRow label={t(StringKeys.FIRMWARE_VERSION)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {firmwareOutdated && (
                  <SyncProblem sx={{ fontSize: 24, color: COLORS.RED }} />
                )}
                <Typography variant="body1" fontSize=".95rem">
                  {deviceData.firmwareVersion}
                </Typography>
              </Box>
            </DataRow>

            <DataRow label={t(StringKeys.SERIAL_NUMBER)}>
              {isSerialNumberLoading ? (
                <Skeleton
                  data-testid="serial-number-skeleton"
                  variant="text"
                  width={SERIAL_NUMBER_SKELETON_WIDTH}
                  sx={{ fontSize: '.95rem' }}
                />
              ) : (
                <Typography
                  data-testid="serial-number-value"
                  variant="body1"
                  fontSize=".95rem"
                >
                  {serialNumber ?? '--'}
                </Typography>
              )}
            </DataRow>

            <DataRow label={t(StringKeys.MAC_ADDRESS)}>
              <Typography variant="body1" fontSize=".95rem">
                {deviceData.macAddress}
              </Typography>
            </DataRow>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default DeviceInfoSection;
